/**
 * Which modules the left rail lists — one decision for the whole SITE, made by
 * its administrator, not each person. The rail is the shape of the product a
 * team works in, so it is a house setting: stored server-side per ERP
 * connection and applied to everyone signed in to that site.
 *
 * Stored as the set of HIDDEN ids. The default (nothing hidden) shows every
 * module, and a module added in a later release appears on its own rather than
 * waiting to be switched on. Loaded once at app start. The administrator edits
 * it under Settings → Sidebar; nobody else can.
 */

import { reactive, ref } from "vue";
import { api } from "@/lib/api";

export interface SidebarModule {
  id: string;
  label: string;
  /** Only ever shown to administrators to begin with. */
  admin?: boolean;
}

/** The toggleable rail modules, in rail order. `id` matches the rail's own ids in Genie.vue. */
export const SIDEBAR_MODULES: SidebarModule[] = [
  { id: "home", label: "Home" },
  { id: "analytics", label: "Analytics" },
  { id: "chat", label: "Chat" },
  { id: "approvals", label: "Approvals" },
  { id: "documents", label: "Documents" },
  { id: "automations", label: "Automations" },
  { id: "inbox", label: "Inbox" },
  { id: "knowledge", label: "Knowledge" },
  { id: "skills", label: "Skill playbooks" },
  { id: "request-analytics", label: "Request analytics", admin: true },
  { id: "views-gallery", label: "Views gallery", admin: true },
  { id: "ops", label: "Operations", admin: true },
];

const state = reactive<{ hidden: Set<string> }>({ hidden: new Set() });

/** True only for the site's administrator — the only person who may change this. */
export const sidebarModulesCanEdit = ref(false);

/** Whether a module is hidden from the rail on this site. Reactive. */
export function isModuleHidden(id: string): boolean {
  return state.hidden.has(id);
}

function apply(hidden: string[]): void {
  state.hidden = new Set(hidden);
}

/** Read the site's setting. Called at app start; failure leaves everything shown. */
export async function loadSidebarModules(): Promise<void> {
  try {
    const value = await api.sidebarModules();
    apply(value.hidden ?? []);
    sidebarModulesCanEdit.value = value.canEdit === true;
  } catch {
    // Signed out, offline, or a server that predates this: the rail is whole.
  }
}

/**
 * Show or hide one module for the whole site (administrator only). Optimistic,
 * reverted if the site refuses it.
 */
export async function setSidebarModule(id: string, show: boolean): Promise<void> {
  if (!sidebarModulesCanEdit.value) return;
  const previous = state.hidden;
  const next = new Set(previous);
  if (show) next.delete(id);
  else next.add(id);
  state.hidden = next;
  try {
    apply((await api.saveSidebarModules({ hidden: [...next] })).hidden ?? []);
  } catch {
    state.hidden = previous;
  }
}
